/**
 * Vera Analytics - the module's shared UI pieces. Same chart doctrine as the Rx
 * kit it is modeled on: every chart is a SINGLE series, drawn in the module
 * accent, with the numbers available as a table beside or beneath it. No dual
 * axes, no categorical palettes, no colour carrying meaning on its own; where
 * a reader compares measures they switch the metric, not the axis.
 */

import { type ReactNode } from 'react';
import { makeStyles, mergeClasses, tokens, Tooltip } from '@fluentui/react-components';
import { InfoRegular } from '@fluentui/react-icons';

/** Dusk Blue: an approved dark brand secondary (light foreground per s4.5). */
export const ANALYTICS_ACCENT = '#3F4C5C';

/* ------------------------------------------------------------- formatting */

export function usd(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'n/a';
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: digits,
  });
}

export function compactUsd(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'n/a';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(0)}k`;
  return usd(value);
}

export function count(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'n/a';
  return value.toLocaleString('en-US');
}

/** 'YYYY-MM' -> 'Jan 26'. */
export function monthLabel(month: string): string {
  const [year, mm] = month.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const index = Number(mm) - 1;
  return `${names[index] ?? mm} ${(year ?? '').slice(2)}`;
}

/* ----------------------------------------------------------------- styles */

const useStyles = makeStyles({
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px',
  },
  tile: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '14px 16px',
  },
  tileLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  tileInfo: {
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'help',
    fontSize: '13px',
    textTransform: 'none',
  },
  tileValue: {
    fontSize: '24px',
    fontWeight: tokens.fontWeightSemibold,
    marginTop: '4px',
    fontVariantNumeric: 'tabular-nums',
  },
  tileNote: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2, marginTop: '2px' },

  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '18px 20px',
  },
  cardHeader: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px' },
  cardTitle: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
  cardHint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },

  barRow: { display: 'grid', gridTemplateColumns: '160px 1fr 110px', alignItems: 'center', gap: '10px', margin: '6px 0' },
  barLabel: { fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  barTrack: { backgroundColor: tokens.colorNeutralBackground4, borderRadius: '4px', height: '14px' },
  barFill: { height: '14px', borderRadius: '0 4px 4px 0', backgroundColor: ANALYTICS_ACCENT },
  barValue: { fontSize: tokens.fontSizeBase300, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: tokens.colorNeutralForeground2 },

  columns: { display: 'flex', alignItems: 'flex-end', gap: '4px', marginTop: '12px' },
  column: { flex: '1 1 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', minWidth: 0 },
  columnStack: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '2px',
  },
  columnValue: {
    fontSize: '9px',
    lineHeight: '12px',
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  // The track carries the height so the bar can be sized in pixels (a
  // percentage would resolve against an indefinite flex parent and collapse;
  // the Rx kit learned this on 2026-08-17).
  columnTrack: { width: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  columnFill: { width: '100%', backgroundColor: ANALYTICS_ACCENT, borderRadius: '4px 4px 0 0', minHeight: '2px' },
  columnLabel: { fontSize: '10px', color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap' },

  empty: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300, padding: '12px 0' },
});

/* ------------------------------------------------------------- components */

export function StatTiles({ children }: { children: ReactNode }) {
  const styles = useStyles();
  return <div className={styles.tiles}>{children}</div>;
}

/** One number, and on request the arithmetic behind it (Rx-kit convention). */
export function StatTile({
  label,
  value,
  note,
  explain,
}: {
  label: string;
  value: string;
  note?: string;
  explain?: string;
}) {
  const styles = useStyles();
  return (
    <div className={styles.tile}>
      <div className={styles.tileLabel}>
        {label}
        {explain ? (
          <Tooltip content={explain} relationship="label" withArrow>
            <span className={styles.tileInfo} tabIndex={0}>
              <InfoRegular />
            </span>
          </Tooltip>
        ) : null}
      </div>
      <div className={styles.tileValue}>{value}</div>
      {note ? <div className={styles.tileNote}>{note}</div> : null}
    </div>
  );
}

export function Card({
  title,
  hint,
  action,
  children,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  const styles = useStyles();
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>{title}</div>
          {hint ? <div className={styles.cardHint}>{hint}</div> : null}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export interface BarDatum {
  key: string;
  label: string;
  value: number | null;
  caption?: string;
}

/** Horizontal bars for a categorical magnitude: one series, one hue, values on the row. */
export function BarList({ data, format = count }: { data: BarDatum[]; format?: (v: number | null) => string }) {
  const styles = useStyles();
  const max = Math.max(...data.map((d) => Math.abs(d.value ?? 0)), 1);
  if (!data.length) return <div className={styles.empty}>Nothing in this window.</div>;
  return (
    <div>
      {data.map((datum) => (
        <div key={datum.key} className={styles.barRow}>
          <Tooltip content={datum.caption ?? datum.label} relationship="label" withArrow>
            <div className={styles.barLabel}>{datum.label}</div>
          </Tooltip>
          <div className={styles.barTrack}>
            <div
              className={styles.barFill}
              style={{ width: `${Math.max((Math.abs(datum.value ?? 0) / max) * 100, 1)}%` }}
            />
          </div>
          <div className={styles.barValue}>{format(datum.value)}</div>
        </div>
      ))}
    </div>
  );
}

export interface TrendPoint {
  key: string;
  label: string;
  value: number | null;
}

/** Plot height in pixels, so the bars can be sized in pixels too. */
const TRACK_HEIGHT = 150;

/**
 * Monthly columns. Single series by design; compare measures by switching the
 * metric. Every labeled column carries its VALUE on top of the bar (Sasha,
 * 9/04: the number is the actual answer, put it on the chart); `labelEvery`
 * thins both the value and the month label together for narrow charts, the
 * most recent month is always labeled, and every column keeps its full
 * label + value in the hover tooltip.
 */
export function TrendColumns({
  data,
  format = count,
  labelEvery = 1,
}: {
  data: TrendPoint[];
  format?: (v: number | null) => string;
  labelEvery?: number;
}) {
  const styles = useStyles();
  if (!data.length) return <div className={styles.empty}>No months in this window.</div>;

  const values = data.map((point) => Math.abs(point.value ?? 0));
  const max = Math.max(...values);
  const anyValue = values.some((value) => value > 0);
  const step = Math.max(1, Math.trunc(labelEvery));

  return (
    <div className={styles.columns}>
      {data.map((point, index) => {
        const value = point.value ?? 0;
        // The tallest bar leaves room for its own value label inside the track.
        const height = !anyValue || max <= 0 ? 2 : Math.max((Math.abs(value) / max) * (TRACK_HEIGHT - 14), 2);
        // Count back from the newest column so "now" always carries a label.
        const labeled = (data.length - 1 - index) % step === 0;
        return (
          <Tooltip key={point.key} content={`${point.label}: ${format(point.value)}`} relationship="label" withArrow>
            <div className={styles.column}>
              <div className={styles.columnTrack} style={{ height: `${TRACK_HEIGHT}px` }}>
                <div className={styles.columnStack}>
                  <div className={styles.columnValue}>{labeled ? format(point.value) : ''}</div>
                  <div className={styles.columnFill} style={{ height: `${height}px` }} />
                </div>
              </div>
              <div className={styles.columnLabel}>{labeled ? point.label : ' '}</div>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
}

export function useAnalyticsStyles() {
  return useStyles();
}

/**
 * Table styling per the platform rule: numeric columns right-justified with
 * the HEADER right-justified over them (Fluent nests the label in an inner
 * button), and faint vertical rules. Compose with mergeClasses, never a
 * template literal - Griffel classnames are atomic.
 */
export const useTableStyles = makeStyles({
  headerCell: {
    whiteSpace: 'nowrap',
    borderRight: `1px solid ${tokens.colorNeutralStroke3}`,
    ':last-child': { borderRight: 'none' },
  },
  cell: {
    borderRight: `1px solid ${tokens.colorNeutralStroke3}`,
    ':last-child': { borderRight: 'none' },
  },
  right: {
    textAlign: 'right',
    '& .fui-TableHeaderCell__button': { justifyContent: 'flex-end', width: '100%' },
  },
  num: { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
});

export { mergeClasses };
